import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { markStudentResultSaved } from "../../Redux/Slices/studentResuilt.slice";
import { getValue } from "../../utils/object.utils";
import { buildStudentResultPayload } from "../../Services/results/results";
import { useSaveStudentResultInBackground } from "./studentResult.hooks";

/** Longer than the shared lodash default so rapid cell edits settle first. */
export const STUDENT_RESULT_AUTOSAVE_DELAY_MS = 2500;
const DEADLOCK_RETRY_LIMIT = 2;
const DEADLOCK_RETRY_BASE_MS = 400;

const isDeadlockError = (error: any) => {
  const nested = error?.response?.data;
  const errno =
    error?.errno ?? nested?.errno ?? nested?.error?.errno ?? nested?.code;
  const code = error?.code ?? nested?.code ?? nested?.error?.code;
  const sqlState = error?.sqlState ?? nested?.sqlState;
  const message = `${error?.sqlMessage || ""} ${error?.message || ""} ${
    nested?.message || ""
  } ${nested?.sqlMessage || ""}`;

  return (
    errno === 1213 ||
    code === "ER_LOCK_DEADLOCK" ||
    sqlState === "40001" ||
    /deadlock/i.test(message)
  );
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Per-student debounce, then a single-flight save queue.
 *
 * Editing A and B starts independent timers. When a timer fires the student
 * is enqueued. Only one individual save is in flight at a time so MySQL
 * does not deadlock on overlapping `result_entries` writes.
 */
export const useDebouncedStudentResultAutosave = ({
  resultId,
  delay = STUDENT_RESULT_AUTOSAVE_DELAY_MS,
  paused = false,
}: {
  resultId: number;
  delay?: number;
  paused?: boolean;
}) => {
  const dispatch = useDispatch();
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const queueRef = useRef<number[]>([]);
  const inFlightRef = useRef(false);
  const previousRevisionsRef = useRef<Record<number, number>>({});
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const { studentList, dirtyRevisions } = useSelector((state: RootState) => ({
    studentList: getValue(
      state.studentResult.paginatedStudentResultList,
      "result",
      [],
    ) as any[],
    dirtyRevisions: state.studentResult.dirtyRevisions,
  }));

  const studentListRef = useRef(studentList);
  studentListRef.current = studentList;

  const resultIdRef = useRef(resultId);
  resultIdRef.current = resultId;

  const { mutateAsync: saveStudentResultAsync } =
    useSaveStudentResultInBackground();

  const saveStudentResultAsyncRef = useRef(saveStudentResultAsync);
  saveStudentResultAsyncRef.current = saveStudentResultAsync;

  const saveStudentWithRetry = useCallback(
    async (studentId: number) => {
      const student = studentListRef.current.find(
        (item) => getValue(item, "student_id") === studentId,
      );
      if (!student) return;

      const payload = buildStudentResultPayload({
        resultId: resultIdRef.current,
        student,
      });
      if (!payload) return;

      let attempt = 0;
      while (attempt <= DEADLOCK_RETRY_LIMIT) {
        try {
          await saveStudentResultAsyncRef.current(payload);
          dispatch(markStudentResultSaved({ studentId }));
          return;
        } catch (error) {
          const canRetry =
            isDeadlockError(error) && attempt < DEADLOCK_RETRY_LIMIT;
          if (!canRetry) {
            return;
          }
          attempt += 1;
          await wait(DEADLOCK_RETRY_BASE_MS * attempt);
        }
      }
    },
    [dispatch],
  );

  const processQueueRef = useRef<() => void>(() => {});

  const processQueue = useCallback(() => {
    if (pausedRef.current || inFlightRef.current) return;

    const studentId = queueRef.current.shift();
    if (studentId === undefined) return;

    inFlightRef.current = true;
    saveStudentWithRetry(studentId).finally(() => {
      inFlightRef.current = false;
      processQueueRef.current();
    });
  }, [saveStudentWithRetry]);

  processQueueRef.current = processQueue;

  const enqueueStudentSave = useCallback((studentId: number) => {
    if (pausedRef.current) return;
    if (!queueRef.current.includes(studentId)) {
      queueRef.current.push(studentId);
    }
    processQueueRef.current();
  }, []);

  const cancelPendingAutosaves = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    queueRef.current = [];
  }, []);

  const scheduleStudentSave = useCallback(
    (studentId: number) => {
      if (pausedRef.current) return;

      const existingTimer = timersRef.current.get(studentId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        timersRef.current.delete(studentId);
        enqueueStudentSave(studentId);
      }, delay);

      timersRef.current.set(studentId, timer);
    },
    [delay, enqueueStudentSave],
  );

  const flushPendingAutosaves = useCallback(() => {
    const pendingStudentIds = Array.from(timersRef.current.keys());
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    pendingStudentIds.forEach((studentId) => {
      if (!queueRef.current.includes(studentId)) {
        queueRef.current.push(studentId);
      }
    });
    processQueueRef.current();
  }, []);

  useEffect(() => {
    Object.entries(dirtyRevisions).forEach(([id, revision]) => {
      const studentId = Number(id);
      if (previousRevisionsRef.current[studentId] !== revision) {
        scheduleStudentSave(studentId);
      }
    });
    previousRevisionsRef.current = { ...dirtyRevisions };
  }, [dirtyRevisions, scheduleStudentSave]);

  useEffect(() => {
    return () => {
      const pendingStudentIds = Array.from(timersRef.current.keys());
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
      pendingStudentIds.forEach((studentId) => {
        if (!queueRef.current.includes(studentId)) {
          queueRef.current.push(studentId);
        }
      });
      processQueueRef.current();
    };
  }, []);

  return {
    cancelPendingAutosaves,
    flushPendingAutosaves,
  };
};
